import { useForm, getFormProps, getInputProps } from "@conform-to/react"
import { getZodConstraint, parseWithZod } from "@conform-to/zod"
import {
	data,
	type LoaderFunctionArgs,
	type ActionFunctionArgs,
} from "react-router"
import { Link, useActionData, useSearchParams } from "react-router"
import { HoneypotInputs } from "remix-utils/honeypot/react"
import { z } from "zod"

import { GeneralErrorBoundary } from "#app/components/error-boundary.tsx"
import { Spacer } from "#app/components/spacer.tsx"
import { Checkbox } from "#app/components/ui/forms/conform-integrated/checkbox.tsx"
import {
	Form,
	FormContext,
	FormControl,
	FormErrorList,
	FormField,
	FormLabel,
	FormMessage,
} from "#app/components/ui/forms/conform-integrated/form"
import { Input } from "#app/components/ui/forms/conform-integrated/input"
import { StatusButton } from "#app/components/ui/status-button.tsx"
import { TypographyH1 } from "#app/components/ui/typography.tsx"
import { login, requireAnonymous } from "#app/utils/auth.server.ts"
import { APP_NAME } from "#app/utils/constants.ts"
import { checkHoneypot } from "#app/utils/honeypot.server.ts"
import { generateMeta } from "#app/utils/meta.js"
import { useIsPending } from "#app/utils/misc.tsx"
import { EmailSchema, PasswordSchema } from "#app/utils/user-validation"
import routes from "#routes/index.js"
import { handleNewSession } from "./login.server"

const LoginFormSchema = z.object({
	email: EmailSchema,
	password: PasswordSchema,
	redirectTo: z.string().optional(),
	remember: z.boolean().optional(),
})

export async function loader({ request }: LoaderFunctionArgs) {
	await requireAnonymous(request)
	return data({})
}

export async function action({ request }: ActionFunctionArgs) {
	await requireAnonymous(request)
	const formData = await request.formData()
	await checkHoneypot(formData)
	const submission = await parseWithZod(formData, {
		schema: intent =>
			LoginFormSchema.transform(async (data, ctx) => {
				if (intent !== null) return { ...data, session: null }

				const session = await login(data)
				if (!session) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: "Credenciales no válidas",
					})
					return z.NEVER
				}

				return { ...data, session }
			}),
		async: true,
	})

	if (submission.status !== "success" || !submission.value.session) {
		return data(
			{
				result: submission.reply({
					hideFields: ["password"],
				}),
			},
			{
				status: submission.status === "error" ? 400 : 200,
			},
		)
	}

	const { session, remember, redirectTo } = submission.value

	return handleNewSession({
		request,
		session,
		remember: remember ?? false,
		redirectTo,
	})
}

export default function LoginPage() {
	const actionData = useActionData<typeof action>()
	const isPending = useIsPending()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get("redirectTo")

	const [form, fields] = useForm({
		id: "login-form",
		constraint: getZodConstraint(LoginFormSchema),
		defaultValue: { redirectTo },
		lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: LoginFormSchema })
		},
		shouldRevalidate: "onBlur",
	})

	return (
		<div className="mx-auto flex min-h-full max-w-sm flex-col justify-center">
			<div className="mx-auto w-full max-w-md">
				<div className="flex flex-col gap-3 text-center">
					<TypographyH1>Accede a tu Cuenta</TypographyH1>
					<p className="mb-8 text-center text-base text-gray-700">
						Inicia sesión para acceder a una experiencia mejorada de compra.
					</p>
				</div>

				<Spacer size="3xs" />

				<div className="flex flex-col gap-4">
					<FormContext context={form.context}>
						<Form
							method="POST"
							{...getFormProps(form)}
							className="flex flex-col gap-4"
						>
							<HoneypotInputs />

							<FormField field={fields.email}>
								<div className="flex flex-col gap-2">
									<FormLabel>Correo electrónico</FormLabel>
									<FormControl>
										<Input
											className="lowercase"
											autoFocus
											autoComplete="email"
											placeholder="panic@thedis.co"
											meta={fields.email}
										/>
									</FormControl>
									<FormMessage />
								</div>
							</FormField>

							<FormField field={fields.password}>
								<div className="flex flex-col gap-2">
									<FormLabel>Contraseña</FormLabel>
									<FormControl>
										<Input
											meta={fields.password}
											type="password"
											autoComplete="current-password"
											placeholder="●●●●●●●●●●"
										/>
									</FormControl>
									<FormMessage />
								</div>
							</FormField>

							<div className="flex justify-between">
								<FormField field={fields.remember}>
									<div className="flex flex-col gap-2">
										<div className="flex items-center gap-2">
											<FormControl>
												<Checkbox meta={fields.remember} />
											</FormControl>
											<FormLabel>Recuérdame</FormLabel>
										</div>
									</div>
								</FormField>
								<div>
									<Link
										to="/forgot-password"
										className="text-body-xs text-muted-foreground hover:underline"
									>
										Has olvidado la contraseña?
									</Link>
								</div>
							</div>

							<input
								{...getInputProps(fields.redirectTo, { type: "hidden" })}
							/>

							<FormErrorList formId={form.id} />

							<div className="flex items-center justify-between gap-6 ">
								<StatusButton
									status={isPending ? "pending" : (form.status ?? "idle")}
									type="submit"
									disabled={isPending}
								>
									Entrar
								</StatusButton>
								<span className="text-center text-sm text-gray-700">
									¿No eres miembro?{" "}
									<Link
										className="text-primary hover:underline"
										to={
											redirectTo
												? `/signup?${encodeURIComponent(redirectTo)}`
												: "/signup"
										}
									>
										Regístrate
									</Link>
									.
								</span>
							</div>
						</Form>
					</FormContext>

					{/* <Separator />

					<ul className="flex flex-col gap-5">
						{providerNames.map(providerName => (
							<li key={providerName}>
								<ProviderConnectionForm
									type="Entrar"
									providerName={providerName}
									redirectTo={redirectTo}
								/>
							</li>
						))}
					</ul> */}
				</div>
			</div>
		</div>
	)
}

export const meta = generateMeta({
	title: `Iniciar sesión | ${APP_NAME}`,
	description: `Inicia sesión en ${APP_NAME} para acceder a una experiencia de compra mejorada.`,
	canonical: routes.public.auth_login.route(),
})

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
